import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';

import { config } from '../../../../config';
import {
  CommunicationPartial,
  CommunicationTemplate,
  Macro,
  MacroResponse,
  PaginatedCommunicationPartial,
  PaginatedCommunicationTemplate,
} from '.';

interface ICommunication {
  type: string;
  label: string;
}

@Injectable()
export class CommunicationService {
  private partialsEndpoint: string = `${config.apiLocation}/admin/communicationPartials`;
  private templatesEndpoint: string = `${config.apiLocation}/admin/communicationTemplates`;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  get communication(): ICommunication {
    const type = this.router.url.split('/')[2];
    switch (type) {
      case 'emails':
        return {
          type: 'email',
          label: 'Email',
        };
      case 'sms':
        return {
          type: 'sms',
          label: 'SMS',
        };
      default:
        return {
          type: '',
          label: '',
        };
    }
  }

  // Communication partial endpoints
  partials(page: number): Observable<PaginatedCommunicationPartial> {
    return this.http
      .get<PaginatedCommunicationPartial>(`${this.partialsEndpoint}?page=${page}`);
  }

  partial(id: string): Observable<CommunicationPartial> {
    return this.http
      .get<{ data: CommunicationPartial }>(`${this.partialsEndpoint}/${id}`)
      .map(res => res.data);
  }

  createPartial(partial: CommunicationPartial): Observable<CommunicationPartial> {
    return this.http
      .post<{ data: CommunicationPartial }>(`${this.partialsEndpoint}`, partial)
      .map(res => res.data);
  }

  updatePartial(partial: RecursivePartial<CommunicationPartial>): Observable<CommunicationPartial> {
    return this.http
      .patch<{ data: CommunicationPartial }>(`${this.partialsEndpoint}/${partial.id}`, partial)
      .map(res => res.data);
  }

  deletePartial(partialId: string) {
    return this.http
      .delete<void>(`${this.partialsEndpoint}/${partialId}`, {});
  }

  // Communication template endpoints
  templates(page: number): Observable<PaginatedCommunicationTemplate> {
    return this.http
      .get<PaginatedCommunicationTemplate>(`${this.templatesEndpoint}?page=${page}&type=${this.communication.type}`);
  }

  template(id: string): Observable<CommunicationTemplate> {
    return this.http
      .get<{ data: CommunicationTemplate }>(`${this.templatesEndpoint}/${id}`)
      .map(res => res.data);
  }

  createTemplate(template: CommunicationTemplate): Observable<CommunicationTemplate> {
    return this.http
      .post<{ data: CommunicationTemplate }>(`${this.templatesEndpoint}`, template)
      .map(res => res.data);
  }

  updateTemplate(template: RecursivePartial<CommunicationTemplate>): Observable<CommunicationTemplate> {
    return this.http
      .patch<{ data: CommunicationTemplate }>(`${this.templatesEndpoint}/${template.id}`, template)
      .map(res => res.data);
  }

  deleteTemplate(templateId: string) {
    return this.http
      .delete<void>(`${this.templatesEndpoint}/${templateId}`, {});
  }

  // Macros endpoints
  partialMacros(): Observable<Macro[]> {
    return this.http
      .get<MacroResponse>(`${config.apiLocation}/admin/partialMacros`)
      .map(res => res.data);
  }

  variableMacros(): Observable<Macro[]> {
    return this.http
      .get<MacroResponse>(`${config.apiLocation}/admin/variableMacros`)
      .map(res => res.data);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/data/communications/communication.service.ts